import { Column, isIconName, Icon, Text, type StylableProps, usePulse } from "@mdotm/shadcn/legacy/components";
import { ForEach } from "@mdotm/utils/react-extra";
import { useAsync } from "@mdotm/utils/promise";
import type { AppDrawerModuleItem } from "./AppDrawer-shared";

function HierarchicalLinksModuleItem({ item, level }: { item: AppDrawerModuleItem; level: number }) {
	const hasAsync = "onClickAsync" in item;
	const [run, asyncState] = useAsync(hasAsync ? (_extra) => item.onClickAsync() : (_extra) => Promise.resolve());
	const errorPulse = usePulse({ trigger: asyncState.kind === "error", duration: 500 });

	const isLoading = asyncState.kind === "loading";
	const Tag = "href" in item ? "a" : "button";

	return (
		<Column flexGrow={0} gap={12}>
			<div
				data-qualifier={item.qualifier}
				className="group flex w-full justify-items-stretch"
				style={{
					paddingLeft: level * 16,
				}}
			>
				<Tag
					className={`flex min-w-0 grow items-center gap-2 py-2 transition-colors rounded-sm px-2 group-hover:bg-(--palette-N100) group-hover:aria-current:bg-(--palette-N600) aria-current:bg-(--palette-N500) aria-current:text-(--palette-N0) ${errorPulse ? "text-(--palette-D500)" : "text-(--palette-N600)"}`}
					aria-current={item.active && !item.children?.some((c) => c.active)}
					data-error-pulse={errorPulse ? "true" : undefined}
					{...("href" in item
						? { href: item.href }
						: {
								onClick: hasAsync ? run : item.onClick,
								type: "button" as const,
								disabled: isLoading,
							})}
				>
					{isLoading ? (
						<Icon icon="calulating" size={18} className="animate-spin ease-in-out" />
					) : typeof item.icon === "string" && isIconName(item.icon) ? (
						<Icon icon={item.icon} size={20} />
					) : (
						item.icon
					)}
					<Text type="Body/M/Medium" color="inherit" className="truncate" title={item.title}>
						{item.title}
					</Text>
				</Tag>
			</div>
			{item.children && item.children.length > 0 && <HierarchicalLinksModule items={item.children} level={level + 1} />}
		</Column>
	);
}

export function HierarchicalLinksModule({
	items,
	level = 0,
	className,
	style,
}: { items: AppDrawerModuleItem[]; level?: number } & StylableProps) {
	return (
		<Column flexGrow={0} gap={12} style={style} className={className}>
			<ForEach collection={items.filter((item) => item.enabled)} keyProvider={(item) => item.name}>
				{(item) => <HierarchicalLinksModuleItem item={item} level={level} />}
			</ForEach>
		</Column>
	);
}
